// Candles of the futures market, the same Binance endpoint the main chart uses
import axios from "axios";

const KLINES_URL = "https://fapi.binance.com/fapi/v1/klines";
const MAX_KLINES = 1500;

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// Native Binance intervals, shortest first
export const KLINE_INTERVALS = [
  { interval: "1m", ms: MINUTE_MS },
  { interval: "3m", ms: 3 * MINUTE_MS },
  { interval: "5m", ms: 5 * MINUTE_MS },
  { interval: "15m", ms: 15 * MINUTE_MS },
  { interval: "30m", ms: 30 * MINUTE_MS },
  { interval: "1h", ms: HOUR_MS },
  { interval: "2h", ms: 2 * HOUR_MS },
  { interval: "4h", ms: 4 * HOUR_MS },
  { interval: "6h", ms: 6 * HOUR_MS },
  { interval: "8h", ms: 8 * HOUR_MS },
  { interval: "12h", ms: 12 * HOUR_MS },
  { interval: "1d", ms: DAY_MS },
  { interval: "3d", ms: 3 * DAY_MS },
  { interval: "1w", ms: 7 * DAY_MS },
];

/**
 * Shortest interval that fits the range into at most `maxCandles` candles.
 * @param {number} rangeMs
 * @param {number} maxCandles
 */
export const pickKlineInterval = (rangeMs, maxCandles) =>
  KLINE_INTERVALS.find(({ ms }) => rangeMs / ms <= maxCandles) ??
  KLINE_INTERVALS[KLINE_INTERVALS.length - 1];

/**
 * @param {object} params
 * @param {string} params.symbol e.g. "BTCUSDT"
 * @param {string} params.interval Binance interval, e.g. "1m"
 * @param {number} params.startTime ms
 * @param {number} [params.endTime] ms
 * @param {number} [params.limit]
 * @returns {Promise<Array<{time: number, open: number, high: number, low: number, close: number}>>}
 */
export const fetchKlines = async ({
  symbol,
  interval,
  startTime,
  endTime,
  limit = MAX_KLINES,
}) => {
  const { data } = await axios.get(KLINES_URL, {
    params: {
      symbol: symbol.toUpperCase(),
      interval,
      startTime: Math.floor(startTime),
      endTime: endTime != null ? Math.floor(endTime) : undefined,
      limit,
    },
  });

  if (!Array.isArray(data)) return [];

  return data.map(([time, open, high, low, close]) => ({
    time,
    open: Number(open),
    high: Number(high),
    low: Number(low),
    close: Number(close),
  }));
};

/**
 * Price of the market at a moment: close of the minute candle it falls in,
 * the last price when the moment is now.
 * @param {string} symbol
 * @param {number} time ms
 */
export const fetchPriceAt = async (symbol, time) => {
  const [candle] = await fetchKlines({
    symbol,
    interval: "1m",
    startTime: time - MINUTE_MS,
    endTime: time,
    limit: 1,
  });

  return candle?.close ?? null;
};
